import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import logo from "@/assets/postflow-logo.png";
import {
  Sparkles, CalendarDays, CheckCircle2, BarChart3, Youtube, Plug,
  Instagram, Facebook, MessageCircle, MapPin, Music2, ArrowRight, ShieldCheck, Fingerprint, Images,
} from "lucide-react";

const CHANNELS = [
  { icon: Instagram, label: "Instagram" }, { icon: Facebook, label: "Facebook" },
  { icon: Youtube, label: "YouTube" }, { icon: Music2, label: "TikTok" },
  { icon: MessageCircle, label: "WhatsApp" }, { icon: MapPin, label: "Google" },
];

const FEATURES = [
  { icon: Fingerprint, title: "DNA da Marca", desc: "Missão, tom de voz, público e palavras-chave orientam cada conteúdo gerado." },
  { icon: Sparkles, title: "Campanhas com IA", desc: "Planejamento editorial de 7, 15 ou 30 dias, com controle anti-repetição." },
  { icon: CheckCircle2, title: "Aprovação", desc: "Fluxo com papéis (RBAC), comentários e agendamento em um clique." },
  { icon: CalendarDays, title: "Calendário Editorial", desc: "Toda a distribuição diária por canal e status em uma visão só." },
  { icon: Youtube, title: "YouTube Studio", desc: "Roteiro, descrição, capítulos, tags e thumbnail para Shorts e vídeos longos." },
  { icon: Images, title: "Biblioteca de Mídia", desc: "Logos, fotos e vídeos com armazenamento em nuvem real." },
  { icon: Plug, title: "Central de Conexões", desc: "Integrações via OAuth oficial — nada é publicado sem confirmação da rede." },
  { icon: BarChart3, title: "Analytics honesto", desc: "Somente métricas reais. O sistema nunca inventa alcance ou engajamento." },
];

const STEPS = ["Marca & DNA", "Campanha IA", "Aprovação", "Calendário", "Publicação"];

export default function Home() {
  const { user } = useAuth();
  const cta = user ? "/app" : "/login";

  return (
    <div className="min-h-screen bg-[#0f172a] text-white overflow-x-hidden">
      {/* Nav */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-[#0f172a]/70 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={logo} alt="PostFlow AI" className="h-9 w-9 rounded-xl bg-white p-1 object-contain" />
            <span className="font-display font-extrabold text-lg">PostFlow AI</span>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm text-slate-300">
            <a href="#recursos" className="hover:text-white transition-colors">Recursos</a>
            <a href="#canais" className="hover:text-white transition-colors">Canais</a>
            <a href="#fluxo" className="hover:text-white transition-colors">Como funciona</a>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="home-login-link" className="text-sm text-slate-300 hover:text-white">Entrar</Link>
            <Link to={cta} data-testid="home-cta-nav" className="pf-gradient text-white text-sm font-semibold rounded-xl px-4 py-2 hover:opacity-95 transition-opacity">
              {user ? "Abrir painel" : "Começar grátis"}
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0 pf-gradient opacity-20 blur-3xl" />
        <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-[#7c3aed]/30 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-20 grid lg:grid-cols-2 gap-12 items-center">
          <div className="pf-fade-up">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-slate-300 mb-6">
              <Sparkles size={13} className="text-[#8b9cff]" /> Conteúdo com IA · multiempresa
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight">
              Crie. Programe. <span className="pf-gradient-text">Publique.</span>
            </h1>
            <p className="text-slate-300 text-base sm:text-lg mt-5 max-w-xl leading-relaxed">
              Todo o seu conteúdo em um só lugar. Planeje campanhas com IA, aprove com a equipe e
              organize o calendário para Instagram, Facebook, YouTube, TikTok, WhatsApp e Google.
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-8">
              <Link to={cta} data-testid="home-cta-primary" className="pf-gradient text-white font-semibold rounded-xl px-6 py-3.5 inline-flex items-center gap-2 hover:opacity-95 transition-all active:scale-[0.98]">
                {user ? "Ir para o painel" : "Começar agora"} <ArrowRight size={18} />
              </Link>
              <Link to="/login" data-testid="home-cta-demo" className="rounded-xl px-6 py-3.5 font-semibold border border-white/15 text-slate-200 hover:bg-white/5 transition-colors">
                Ver conta demo
              </Link>
            </div>
            <div className="flex items-center gap-2 mt-6 text-xs text-slate-400">
              <ShieldCheck size={15} className="text-emerald-400" /> Sem scraping. Sem senhas das redes. Apenas OAuth e APIs oficiais.
            </div>
          </div>

          <div className="relative pf-fade-up" style={{ animationDelay: ".1s" }}>
            <div className="pf-card !bg-white/5 !border-white/10 p-6 backdrop-blur-sm">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2 text-sm font-semibold">Calendário Editorial</div>
                <span className="text-[10px] rounded-full border border-white/10 bg-white/5 text-slate-300 px-2 py-0.5 font-bold">PRÉVIA</span>
              </div>
              <div className="grid grid-cols-4 gap-2">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="rounded-lg bg-white/5 border border-white/5 p-2">
                    <div className="text-[9px] text-slate-500">Dia {i + 1}</div>
                    <div className="h-1.5 rounded-full mt-2 pf-gradient" style={{ opacity: 0.4 + (i % 3) * 0.25 }} />
                    <div className="text-[9px] text-slate-400 mt-2">{CHANNELS[i % CHANNELS.length].label}</div>
                  </div>
                ))}
              </div>
              <div className="mt-5 grid grid-cols-3 gap-3">
                {[["Conteúdos", "30"], ["Aprovados", "18"], ["Agendados", "9"]].map(([l, v]) => (
                  <div key={l} className="rounded-xl bg-white/5 p-3">
                    <div className="text-lg font-extrabold font-display">{v}</div>
                    <div className="text-[10px] text-slate-400 uppercase">{l}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Channels */}
      <section id="canais" className="border-y border-white/5 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
          <p className="text-center text-xs uppercase tracking-widest text-slate-500 mb-6">Publique nos canais que importam</p>
          <div className="flex flex-wrap justify-center gap-3">
            {CHANNELS.map((c) => (
              <div key={c.label} className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-slate-200">
                <c.icon size={16} /> {c.label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="recursos" className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <div className="max-w-2xl">
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Tudo o que sua operação de conteúdo precisa</h2>
          <p className="text-slate-400 mt-3">Da ideia à publicação, com governança, papéis e transparência total sobre o que é real.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
          {FEATURES.map((f, i) => (
            <div key={f.title} data-testid={`home-feature-${i}`} className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 hover:bg-white/[0.06] hover:-translate-y-1 transition-all">
              <div className="h-11 w-11 rounded-xl pf-gradient flex items-center justify-center mb-4"><f.icon size={20} /></div>
              <h3 className="font-display font-bold">{f.title}</h3>
              <p className="text-sm text-slate-400 mt-1.5 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Flow */}
      <section id="fluxo" className="border-t border-white/5 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-center">Do briefing ao calendário em minutos</h2>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-12">
            {STEPS.map((s, i) => (
              <div key={s} className="flex items-center gap-3">
                <div className="rounded-2xl border border-white/10 bg-white/5 px-5 py-4 text-center">
                  <div className="h-8 w-8 rounded-full pf-gradient flex items-center justify-center text-sm font-bold mx-auto mb-2">{i + 1}</div>
                  <div className="text-sm font-medium">{s}</div>
                </div>
                {i < STEPS.length - 1 && <ArrowRight size={18} className="text-slate-600 hidden sm:block" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <div className="relative rounded-3xl overflow-hidden border border-white/10 p-10 sm:p-14 text-center">
          <div className="absolute inset-0 pf-gradient opacity-20" />
          <div className="relative">
            <img src={logo} alt="PostFlow AI" className="h-16 w-16 mx-auto rounded-2xl bg-white p-1.5 object-contain mb-6" />
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold">Comece a criar hoje</h2>
            <p className="text-slate-300 mt-3 max-w-xl mx-auto">Organize todo o seu conteúdo com IA, multiempresa e controle total. Experimente com a conta demo.</p>
            <Link to={cta} data-testid="home-cta-bottom" className="inline-flex items-center gap-2 mt-8 pf-gradient text-white font-semibold rounded-xl px-7 py-3.5 hover:opacity-95 transition-all active:scale-[0.98]">
              {user ? "Abrir painel" : "Criar conta grátis"} <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-500">
          <div className="flex items-center gap-2"><img src={logo} alt="" className="h-6 w-6 rounded-lg bg-white p-0.5 object-contain" /> PostFlow AI · Crie. Programe. Publique.</div>
          <nav className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
            <Link to="/termos" data-testid="footer-termos" className="hover:text-white transition-colors">Termos de Serviço</Link>
            <Link to="/privacidade" data-testid="footer-privacidade" className="hover:text-white transition-colors">Privacidade</Link>
            <Link to="/exclusao-de-dados" data-testid="footer-exclusao" className="hover:text-white transition-colors">Exclusão de Dados</Link>
          </nav>
          <div>© {new Date().getFullYear()} PostFlow AI</div>
        </div>
      </footer>
    </div>
  );
}
